import api from './api';
import { Customer, ApiResponse, PaginatedResponse } from '../types';

export interface StatusSyncItem {
  id: string;
  customer_code: string;
  name: string;
  username: string;
  dbStatus: string;
  dbEnabled: boolean;
  mikrotikEnabled: boolean | null;
  routerName: string | null;
  syncStatus: 'synced' | 'mismatch' | 'not_found' | 'error';
  error?: string;
}

class CustomerService {
  async getCustomers(
    page = 1,
    limit: number | 'all' = 10,
    search?: string,
    filters?: { spop_id?: string; status?: string }
  ): Promise<PaginatedResponse<Customer>> {
    let url = `/customers?page=${page}&limit=${limit === 'all' ? 'all' : limit}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (filters?.spop_id) {
      url += `&spop_id=${filters.spop_id}`;
    }
    if (filters?.status) {
      url += `&status=${filters.status}`;
    }
    const response = await api.get<PaginatedResponse<Customer>>(url);
    return response.data;
  }

  async getCustomer(id: number): Promise<Customer> {
    const response = await api.get<ApiResponse<Customer>>(`/customers/${id}`);
    return response.data.data;
  }

  async createCustomer(customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'Package' | 'Router'>): Promise<Customer> {
    const response = await api.post<ApiResponse<Customer>>('/customers', customerData);
    return response.data.data;
  }

  async updateCustomer(id: number, customerData: Partial<Customer>): Promise<Customer> {
    const response = await api.put<ApiResponse<Customer>>(`/customers/${id}`, customerData);
    return response.data.data;
  }

  async deleteCustomer(id: number): Promise<void> {
    await api.delete(`/customers/${id}`);
  }

  async activateCustomer(id: number): Promise<Customer> {
    const response = await api.post<ApiResponse<Customer>>(`/customers/${id}/activate`);
    return response.data.data;
  }

  async deactivateCustomer(id: number): Promise<Customer> {
    const response = await api.post<ApiResponse<Customer>>(`/customers/${id}/deactivate`);
    return response.data.data;
  }

  async disconnectCustomer(id: number): Promise<void> {
    await api.post(`/customers/${id}/disconnect`);
  }

  async resetPassword(id: number, newPassword: string): Promise<void> {
    await api.post(`/customers/${id}/reset-password`, { password: newPassword });
  }

  async checkCustomerCodeExists(customerCode: string): Promise<boolean> {
    try {
      // Get all customers and check if the code exists
      const customers = await this.getCustomers(1, 1000);
      const customerList = customers.data.customers || [];
      return customerList.some((customer: any) => customer.customer_code === customerCode);
    } catch (error) {
      console.error('Error checking customer code:', error);
      return false; // Assume it doesn't exist if check fails
    }
  }

  async generateUniqueCustomerCode(spopCode: string, odpCode: string): Promise<string> {
    const basePattern = `${spopCode}${odpCode}`;
    let attempts = 0;
    const maxAttempts = 100;
    
    while (attempts < maxAttempts) {
      attempts++;
      const nextNumber = await this.getNextCustomerNumber(spopCode, odpCode);
      const adjustedNumber = nextNumber + attempts - 1;
      const formattedNumber = String(adjustedNumber).padStart(3, '0');
      const customerCode = `${basePattern}${formattedNumber}`;
      
      const exists = await this.checkCustomerCodeExists(customerCode);
      if (!exists) {
        return customerCode;
      }
    }
    
    // Fallback to timestamp-based code
    const timestamp = Date.now().toString().slice(-6);
    return `${basePattern}${timestamp}`;
  }

  async getNextCustomerNumber(spopCode: string, odpCode: string): Promise<number> {
    try {
      // Get all customers with comprehensive search
      const customers = await this.getCustomers(1, 1000);
      const customerList = customers.data.customers || [];
      
      // Create pattern for exact matching (no separators)
      const exactPattern = `${spopCode}${odpCode}`;
      
      // Extract numbers from customer codes that match the exact pattern
      const existingNumbers = customerList
        .filter((customer: any) => {
          const code = customer.customer_code;
          // Check if code starts with exact pattern and has numeric suffix
          return code && code.startsWith(exactPattern) && /^\d+$/.test(code.slice(exactPattern.length));
        })
        .map((customer: any) => {
          const code = customer.customer_code;
          const numberPart = code.slice(exactPattern.length);
          return parseInt(numberPart) || 0;
        })
        .filter((num: number) => !isNaN(num) && num > 0)
        .sort((a: number, b: number) => a - b); // Sort ascending to find gaps
      
      console.log('Existing numbers for pattern', exactPattern, ':', existingNumbers);
      
      // Find the first available number (starting from 1)
      let nextNumber = 1;
      for (const existingNum of existingNumbers) {
        if (existingNum === nextNumber) {
          nextNumber++;
        } else if (existingNum > nextNumber) {
          break; // Found a gap, use the current nextNumber
        }
      }
      
      console.log('Next available number:', nextNumber);
      return nextNumber;
    } catch (error) {
      console.error('Error getting next customer number:', error);
      // Fallback to timestamp-based number
      return Date.now() % 1000 + 1; // Ensure it's not 0
    }
  }

  async syncPPPSecret(routerId: string, username: string): Promise<{
    username: string;
    password: string;
    profile: string;
    comment: string;
    action: string;
  }> {
    const response = await api.post<ApiResponse<{
      username: string;
      password: string;
      profile: string;
      comment: string;
      action: string;
    }>>('/customers/sync-ppp', {
      router_id: routerId,
      username: username
    });
    return response.data.data;
  }

  async updatePPPSecret(customerId: string | number, customerData: Partial<Customer>): Promise<{
    customer: Customer;
    pppUpdateResult?: {
      success: boolean;
      message: string;
      action: string;
    };
  }> {
    const response = await api.put<ApiResponse<{
      customer: Customer;
      pppUpdateResult?: {
        success: boolean;
        message: string;
        action: string;
      };
    }>>(`/customers/${customerId}/update-with-ppp`, customerData);
    return response.data.data;
  }

  async fetchPPPSecrets(customerId: string): Promise<any[]> {
    try {
      const response = await api.get<ApiResponse<any[]>>(`/customers/${customerId}/ppp-secrets`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching PPP secrets:', error);
      // Return mock data as fallback
      return [{
        id: '1',
        username: 'customer_username',
        profile: 'Default Profile',
        status: 'offline',
        ipAddress: null,
        uptime: null,
        callerID: null,
        lastLogin: null
      }];
    }
  }

  // Excel Import Methods
  async downloadCustomerTemplate(): Promise<Blob> {
    const response = await api.get('/excel-import/template', {
      responseType: 'blob',
    });
    return response.data;
  }

  async validateCustomerExcel(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('excelFile', file);
    
    const response = await api.post('/excel-import/validate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async previewCustomerExcel(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('excelFile', file);
    
    const response = await api.post('/excel-import/preview', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async importCustomersFromExcel(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('excelFile', file);
    
    const response = await api.post('/excel-import/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getImportStats(): Promise<any> {
    const response = await api.get('/excel-import/stats');
    return response.data;
  }

  async checkStatusSync(customerIds: string[]): Promise<{
    total: number;
    synced: number;
    mismatched: number;
    notFound: number;
    errors: number;
    items: StatusSyncItem[];
  }> {
    const response = await api.post<
      ApiResponse<{
        total: number;
        synced: number;
        mismatched: number;
        notFound: number;
        errors: number;
        items: StatusSyncItem[];
      }>
    >('/customers/sync-status/check', { customer_ids: customerIds });
    return response.data.data;
  }

  async exportStatusSync(
    customerIds: string[],
    options?: { scopeLabel?: string; filter?: 'all' | 'issues'; items?: StatusSyncItem[] }
  ): Promise<void> {
    const response = await api.post(
      '/customers/sync-status/export',
      {
        customer_ids: customerIds,
        scope_label: options?.scopeLabel,
        filter: options?.filter || 'all',
        items: options?.items,
      },
      { responseType: 'blob' }
    );

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
    link.download = `sync_status_db_mikrotik_${timestamp}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  async fixStatusSync(
    customerIds: string[],
    mode: 'db_to_mikrotik' | 'mikrotik_to_db'
  ): Promise<{
    fixed: number;
    failed: number;
    results: { id: string; success: boolean; appliedStatus?: string; error?: string }[];
  }> {
    const response = await api.post<
      ApiResponse<{
        fixed: number;
        failed: number;
        results: { id: string; success: boolean; appliedStatus?: string; error?: string }[];
      }>
    >('/customers/sync-status/fix', { customer_ids: customerIds, mode });
    return response.data.data;
  }

  async bulkUpdateStatus(
    customerIds: string[],
    status: 'active' | 'inactive' | 'suspended' | 'free' | 'NonInternet'
  ): Promise<{
    updated: number;
    failed: number;
    results: { id: string; success: boolean; status?: string; error?: string }[];
  }> {
    const response = await api.patch<
      ApiResponse<{
        updated: number;
        failed: number;
        results: { id: string; success: boolean; status?: string; error?: string }[];
      }>
    >('/customers/bulk/status', { customer_ids: customerIds, status });
    return response.data.data;
  }

  async exportCustomers(search?: string, filters?: { spop_id?: string; status?: string }): Promise<void> {
    let url = '/customers/export';
    const params = new URLSearchParams();
    
    if (search) {
      params.append('search', search);
    }
    if (filters?.spop_id) {
      params.append('spop_id', filters.spop_id);
    }
    if (filters?.status) {
      params.append('status', filters.status);
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await api.get(url, {
      responseType: 'blob',
    });
    
    // Create download link
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    
    // Generate filename with timestamp
    const now = new Date();
    const timestamp = now.toISOString().slice(0, 19).replace(/[:-]/g, '');
    let filename = `customers_export_${timestamp}.xlsx`;
    
    // Add filter info to filename
    if (search || filters?.status || filters?.spop_id) {
      let filterInfo = '';
      if (search) filterInfo += `_search`;
      if (filters?.status) filterInfo += `_${filters.status}`;
      if (filters?.spop_id) filterInfo += `_spop${filters.spop_id}`;
      filename = `customers_export${filterInfo}_${timestamp}.xlsx`;
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

const customerService = new CustomerService();
export default customerService;
